import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Label,
  Textarea,
  Dropdown,
  Option,
  Checkbox,
  Field,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Lightbulb24Regular,
  CheckmarkCircle24Filled,
  ErrorCircle24Filled,
} from '@fluentui/react-icons';
import { AreaConfig } from '../types';
import { addPrompt, addArea, AddPromptRequest, AddAreaRequest } from '../services/customPromptsService';
import { extractParameters, validateParameterSyntax, formatParameterName } from '../utils/promptParameters';
import { configService } from '../services/configService';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  newAreaSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
    marginTop: '8px',
  },
  previewSection: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: '12px',
  },
  previewLabel: {
    fontWeight: '600',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  parameterList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  parameterChip: {
    padding: '4px 8px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
  },
  statusMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '14px',
  },
  successMessage: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  errorMessage: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  colorPickerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  colorPreview: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: `2px solid ${tokens.colorNeutralStroke1}`,
  },
});

interface AddPromptModalProps {
  open: boolean;
  onClose: () => void;
  areas: AreaConfig[];
  onPromptAdded?: () => void;
}

const AVAILABLE_ICONS = [
  'Chat',
  'Money',
  'ReceiptMoney',
  'Briefcase',
  'ShoppingCart',
  'Box',
  'People',
  'Calendar',
  'Document',
  'Settings',
];

const ICON_COLORS = [
  '#0078D4', // Blue
  '#107C10', // Green
  '#B8432F', // Red
  '#881798', // Purple
  '#FF8C00', // Orange
  '#E3008C', // Magenta
  '#00BCF2', // Cyan
];

export const AddPromptModal: React.FC<AddPromptModalProps> = ({
  open,
  onClose,
  areas,
  onPromptAdded,
}) => {
  const styles = useStyles();

  // Form state
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [category, setCategory] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [createNewArea, setCreateNewArea] = useState(false);

  // New area state
  const [newAreaName, setNewAreaName] = useState('');
  const [newAreaDescription, setNewAreaDescription] = useState('');
  const [newAreaIcon, setNewAreaIcon] = useState(AVAILABLE_ICONS[0]);
  const [newAreaColor, setNewAreaColor] = useState(ICON_COLORS[0]);

  // UI state
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);
  const [detectedParams, setDetectedParams] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Extract parameters when text changes
  useEffect(() => {
    if (text) {
      const syntaxError = validateParameterSyntax(text);
      setValidationError(syntaxError);

      if (!syntaxError) {
        const params = extractParameters(text);
        setDetectedParams(params);
      } else {
        setDetectedParams([]);
      }
    } else {
      setDetectedParams([]);
      setValidationError(null);
    }
  }, [text]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle('');
      setText('');
      setCategory('');
      setSelectedAreaId(areas.length > 0 ? areas[0].id : '');
      setCreateNewArea(false);
      setNewAreaName('');
      setNewAreaDescription('');
      setNewAreaIcon(AVAILABLE_ICONS[0]);
      setNewAreaColor(ICON_COLORS[0]);
      setMessage(null);
      setValidationError(null);
      setDetectedParams([]);
    }
  }, [open, areas]);

  const handleSave = async () => {
    // Validation
    if (!title.trim()) {
      setMessage({ success: false, text: 'Prompt title is required' });
      return;
    }
    if (!text.trim()) {
      setMessage({ success: false, text: 'Prompt text is required' });
      return;
    }
    if (validationError) {
      setMessage({ success: false, text: validationError });
      return;
    }
    if (!createNewArea && !selectedAreaId) {
      setMessage({ success: false, text: 'Please select an area or create a new one' });
      return;
    }
    if (createNewArea) {
      if (!newAreaName.trim()) {
        setMessage({ success: false, text: 'Area name is required' });
        return;
      }
      if (!newAreaDescription.trim()) {
        setMessage({ success: false, text: 'Area description is required' });
        return;
      }
    }

    setSaving(true);
    setMessage(null);

    try {
      let targetAreaId = selectedAreaId;

      // Create new area if needed
      if (createNewArea) {
        const newArea: AddAreaRequest = {
          name: newAreaName.trim(),
          description: newAreaDescription.trim(),
          icon: newAreaIcon,
          color: newAreaColor,
        };

        const createdArea = await addArea(newArea);
        targetAreaId = createdArea.id;
      }

      // Create the prompt
      const promptRequest: AddPromptRequest = {
        title: title.trim(),
        text: text.trim(),
        category: category.trim() || 'Custom',
      };

      await addPrompt(targetAreaId, promptRequest);

      setMessage({
        success: true,
        text: createNewArea
          ? `Successfully created area "${newAreaName}" and added prompt "${title}"`
          : `Successfully added prompt "${title}"`,
      });

      // Clear cache to reload prompts
      configService.clearCache();

      // Notify parent
      if (onPromptAdded) {
        setTimeout(() => {
          onPromptAdded();
        }, 1000);
      }

      // Close modal after delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to add prompt:', error);
      setMessage({
        success: false,
        text: error instanceof Error ? error.message : 'Failed to add prompt',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !saving && onClose()}>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogTitle
          action={
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              disabled={saving}
            />
          }
        >
          Add Custom Prompt
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.dialogContent}>
            {/* Prompt Title */}
            <Field label="Prompt Title" required>
              <Input
                value={title}
                onChange={(_, data) => setTitle(data.value)}
                placeholder="e.g., Check Inventory Levels"
                disabled={saving}
                maxLength={100}
              />
            </Field>

            {/* Prompt Text */}
            <Field
              label="Prompt Text"
              required
              hint="Use {variable} for parameters (e.g., {warehouse}, {customer})"
            >
              <Textarea
                value={text}
                onChange={(_, data) => setText(data.value)}
                placeholder="e.g., Show me inventory levels for warehouse {warehouse}"
                disabled={saving}
                rows={4}
                maxLength={2000}
                resize="vertical"
              />
            </Field>

            {/* Parameter Preview */}
            {detectedParams.length > 0 && (
              <div className={styles.previewSection}>
                <div className={styles.previewLabel}>
                  <Lightbulb24Regular />
                  <Text weight="semibold">Detected Parameters</Text>
                </div>
                <div className={styles.parameterList}>
                  {detectedParams.map((param) => (
                    <div key={param} className={styles.parameterChip}>
                      {formatParameterName(param)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category */}
            <Field label="Category" hint="Optional - used for grouping prompts">
              <Input
                value={category}
                onChange={(_, data) => setCategory(data.value)}
                placeholder="e.g., Inventory, Finance"
                disabled={saving}
                maxLength={50}
              />
            </Field>

            {/* Area Selection */}
            <Field label="Area" required>
              <Dropdown
                value={areas.find((a) => a.id === selectedAreaId)?.name || ''}
                onOptionSelect={(_, data) => setSelectedAreaId(data.optionValue || '')}
                disabled={createNewArea || saving}
                placeholder="Select an area"
              >
                {areas.map((area) => (
                  <Option key={area.id} value={area.id}>
                    {area.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            {/* Create New Area Checkbox */}
            <Checkbox
              label="Create new area"
              checked={createNewArea}
              onChange={(_, data) => setCreateNewArea(!!data.checked)}
              disabled={saving}
            />

            {/* New Area Section */}
            {createNewArea && (
              <div className={styles.newAreaSection}>
                <Text weight="semibold">New Area Details</Text>

                <Field label="Area Name" required>
                  <Input
                    value={newAreaName}
                    onChange={(_, data) => setNewAreaName(data.value)}
                    placeholder="e.g., Custom Inventory"
                    disabled={saving}
                    maxLength={100}
                  />
                </Field>

                <Field label="Description" required>
                  <Textarea
                    value={newAreaDescription}
                    onChange={(_, data) => setNewAreaDescription(data.value)}
                    placeholder="e.g., Custom inventory management prompts"
                    disabled={saving}
                    rows={2}
                    maxLength={500}
                  />
                </Field>

                <Field label="Icon">
                  <Dropdown
                    value={newAreaIcon}
                    onOptionSelect={(_, data) => setNewAreaIcon(data.optionValue || AVAILABLE_ICONS[0])}
                    disabled={saving}
                  >
                    {AVAILABLE_ICONS.map((icon) => (
                      <Option key={icon} value={icon}>
                        {icon}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label="Color">
                  <div className={styles.colorPickerRow}>
                    <Input
                      type="text"
                      value={newAreaColor}
                      onChange={(_, data) => {
                        const value = data.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                          setNewAreaColor(value);
                        }
                      }}
                      disabled={saving}
                      maxLength={7}
                      placeholder="#0078D4"
                    />
                    <div
                      className={styles.colorPreview}
                      style={{ backgroundColor: newAreaColor }}
                    />
                  </div>
                </Field>
              </div>
            )}

            {/* Status Message */}
            {message && (
              <div
                className={`${styles.statusMessage} ${
                  message.success ? styles.successMessage : styles.errorMessage
                }`}
              >
                {message.success ? (
                  <CheckmarkCircle24Filled />
                ) : (
                  <ErrorCircle24Filled />
                )}
                <Text>{message.text}</Text>
              </div>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSave}
            disabled={saving || !!validationError}
          >
            {saving ? <Spinner size="tiny" /> : 'Add Prompt'}
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
